/*
 * Copyright (c) 2026. Bluemate B.V.
 *
 * Simplified review feed for the mobile layout (the bottom-nav breakpoint
 * range, < 1280px): date-grouped full-width cards with the product header,
 * stars and review text, an explicit load-more button instead
 * of the desktop's infinite scroll, and the star-count filter rebuilt on the
 * generic PortalSelect (same primitive as the notification channel selects).
 */
import './styles/reviewFeedMobile.scss';

import React, {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import ProductReview from '../../../models/productReviewModel';

import {
    BluemateBadge,
    BluemateButton,
    BluemateEmptyState,
    BluemateExternalLink,
    BluemateImageFallback,
    BluemateListGroup
} from '../../controls';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import {DotsLoader} from '../../controls/loader/dotsLoader';

import BolRating from '../../bolRating/bolRating';

import {groupReviewsByDate} from './feedGrouping';

import {DefaultNumberObject} from '../../../util/defaultObject';

import {PortalSelectItem, Rating} from '../../../util/types';

import {parseThousandSeparator} from '../../../util/utils';

const ALL_RATINGS: Array<Rating> = [5, 4, 3, 2, 1];

interface Props {
    onChangeRatingsFilter: (ratings: Array<Rating>) => void;
    onLoadMore: (callback: VoidFunction) => void;
    reviews: Array<ProductReview>;
    loading: boolean;
    loadingMore: boolean;
    total: number;
    ratingCounts: DefaultNumberObject<Rating>;
    ratingsFilter: Set<Rating>;
}

export default function ReviewFeedMobile(props: Props) {
    const {t} = useTranslation();
    const {onChangeRatingsFilter, onLoadMore, reviews, loading, loadingMore, total, ratingCounts, ratingsFilter} =
        props;
    const hasMore = reviews.length < total;
    // Stable reference: PortalSelectController re-syncs its internal value on
    // every new `value` reference, so a fresh Array.from per render would cost
    // an extra select render on every feed render.
    const filterValues = useMemo(() => Array.from(ratingsFilter), [ratingsFilter]);

    function onChangeFilter(_rating: Rating | null, _selected: boolean, ratings?: Array<Rating>) {
        if (ratings !== undefined) onChangeRatingsFilter(ratings);
    }

    function buildFilterItems(): Array<PortalSelectItem<Rating>> {
        return ALL_RATINGS.map(rating => {
            const count = ratingCounts.get(rating);
            return {
                value: rating,
                label: (
                    <span className='star-filter-option'>
                        <BolRating value={rating} />
                        {count > -1 && <span className='option-count'>({parseThousandSeparator(count)})</span>}
                    </span>
                )
            };
        });
    }

    function renderFilter() {
        return (
            <div className='mobile-filter-control'>
                <PortalSelectController<Rating>
                    value={filterValues}
                    onChange={onChangeFilter}
                    items={buildFilterItems()}
                    // Constant trigger text — the selection shows via the item
                    // checkboxes, not the trigger label.
                    selectLabel={t('reviewAnalytics.reviewFeed.filterStars', 'Filter aantal sterren')}
                    multiple
                    controls={['selectAll', 'deselectAll']}
                    size='medium'
                    // The select-option auto-scroll (scrollIntoView) also drags
                    // the .page container — it would fight the feed's own
                    // scroll-to-top after a filter change.
                    disableAutoScroll
                    className='star-filter-select'
                    itemsClassName='star-filter-items'
                />
            </div>
        );
    }

    function renderAuthorLine(review: ProductReview) {
        return <label className='review-author'>{review.getAuthorLine(' • ')}</label>;
    }

    function renderReview(review: ProductReview) {
        return (
            <div key={review.id} className='mobile-review-card' id={`review-${review.id}`}>
                <BluemateExternalLink
                    className='card-external-link'
                    href={review.bolProductPageUrl}
                    title={t('reviewAnalytics.reviewFeed.viewOnBol', 'Bekijk product op bol')}
                />
                <div className='card-product-header'>
                    <BluemateImageFallback
                        src={review.imageUrl}
                        className='product-image'
                        imageClassName='product-image'
                    />
                    <div className='product-info'>
                        <label className='product-name'>{review.title}</label>
                        <label className='product-ean'>EAN: {review.ean}</label>
                    </div>
                </div>
                <div className='card-review-content'>
                    <BolRating value={review.rating / 20} />
                    <label className='review-title'>{review.reviewTitle}</label>
                    {!!review.body && <p className='review-body'>{review.body}</p>}
                    <div className='review-footer'>{renderAuthorLine(review)}</div>
                </div>
            </div>
        );
    }

    function renderGroups() {
        return groupReviewsByDate(reviews).map(group => (
            <BluemateListGroup key={group.key} title={group.date.format(ProductReview.dateFormat)}>
                {group.reviews.map(renderReview)}
            </BluemateListGroup>
        ));
    }

    function renderLoadMore() {
        if (loading || loadingMore)
            return (
                <div className='feed-loader-container'>
                    <DotsLoader className='feed-loader' dotSize='3rem' />
                </div>
            );
        if (!hasMore) return null;
        return (
            <div className='load-more-container'>
                <BluemateButton
                    onClick={() => onLoadMore(() => undefined)}
                    label={t('reviewAnalytics.reviewFeed.loadMore', 'Meer laden')}
                    secondary
                    shape='rounded'
                />
            </div>
        );
    }

    function renderEmpty() {
        if (loading || reviews.length) return null;
        return (
            <BluemateEmptyState
                image='data-rows'
                title={t(
                    'reviewAnalytics.reviewFeed.emptyIntro',
                    'Hier zie je straks al jouw productreviews in één handig overzicht.'
                )}
            >
                {t('reviewAnalytics.reviewFeed.emptyCollecting', 'Data aan het verzamelen, kom morgen nog eens terug.')}
            </BluemateEmptyState>
        );
    }

    return (
        <div className='reviews-feed-mobile'>
            <div className='title-row'>
                <label className='col-title'>{t('reviewAnalytics.reviewFeed.title', 'Reviews')}</label>
                {total > 0 && (
                    <BluemateBadge
                        label={parseThousandSeparator(total)}
                        color='blue'
                        outline
                        className='reviews-count'
                        tooltip={t(
                            'reviewAnalytics.reviewFeed.countTooltip',
                            'Het totaal aantal reviews in de lijst hieronder'
                        )}
                    />
                )}
            </div>
            {renderFilter()}
            {renderGroups()}
            {renderEmpty()}
            {renderLoadMore()}
        </div>
    );
}
